import { useRef, useState } from "react";
import { ImagePlus, X } from "lucide-react";
import { MediaStudio } from "../components/MediaStudio";
import { Pills } from "../components/AiHtml";
import { Checkbox } from "../components/ui/checkbox";
import { LogoUploader, uploadAsset } from "../components/Assets";
import { BrandStyleCard } from "../components/BrandStyle";
import { useBrands } from "../context/BrandContext";
import { fileUrl, handleErr } from "../lib/api";

const COST = { "1K": 15, "2K": 25, "4K": 40 };

export default function ImageStudio() {
  const [size, setSize] = useState("1K");
  const [aspect, setAspect] = useState("1:1");
  const { active } = useBrands();
  const [useLogo, setUseLogo] = useState(false);
  const [useStyle, setUseStyle] = useState(true);
  const [refImg, setRefImg] = useState(null);
  const ref = useRef(null);
  const pickRef = async (f) => {
    if (!f) return;
    try { setRefImg(await uploadAsset(f, { brandId: active?.brandId || "" })); } catch (e) { handleErr(e); }
  };
  return (
    <MediaStudio
      type="image" label="Image Studio" title="AI image generation" desc="Ad creatives, product shots and social visuals in up to 4K."
      cost={COST[size]} payload={{ size, aspect, useLogo: useLogo && !!active?.logoAssetId, useStyle, refAssetIds: refImg ? [refImg.assetId] : [] }} waitLabel="Generating your image... (15-60s)"
      controls={
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2"><BrandStyleCard useStyle={useStyle} onToggle={setUseStyle} /></div>
          <div><p className="label-mono mb-2">Size</p><Pills testPrefix="image-size" value={size} onChange={setSize} options={[{ value: "1K", label: "1K · 15cr" }, { value: "2K", label: "2K · 25cr" }, { value: "4K", label: "4K · 40cr" }]} /></div>
          <div><p className="label-mono mb-2">Aspect ratio</p><Pills testPrefix="image-aspect" value={aspect} onChange={setAspect} options={["1:1", "4:5", "9:16", "16:9"]} /></div>
          <div className="space-y-3">
            <p className="label-mono">Brand logo</p>
            <LogoUploader compact />
            <label className={`flex items-center gap-2 text-sm ${active?.logoAssetId ? "text-slate-300" : "text-slate-600"}`}>
              <Checkbox data-testid="image-use-logo" disabled={!active?.logoAssetId} checked={useLogo && !!active?.logoAssetId} onCheckedChange={(v) => setUseLogo(!!v)} /> Put my logo on this image
            </label>
          </div>
          <div className="space-y-3">
            <p className="label-mono">Reference image (optional)</p>
            {refImg ? (
              <div className="flex items-center gap-3" data-testid="image-ref-preview"><img src={fileUrl("asset", refImg.assetId)} alt="" className="w-14 h-14 rounded-lg object-cover" /><span className="text-xs text-slate-400 truncate">{refImg.name}</span><button onClick={() => setRefImg(null)} className="text-slate-500 hover:text-red-400"><X size={14} /></button></div>
            ) : (
              <button data-testid="image-ref-upload-btn" onClick={() => ref.current?.click()} className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-dashed border-slate-700 hover:border-orange-500 text-slate-400"><ImagePlus size={15} /> Upload product photo or style reference</button>
            )}
            <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="image-ref-input" onChange={(e) => { pickRef(e.target.files[0]); e.target.value = ""; }} />
          </div>
        </div>
      }
    />
  );
}
